import React from "react"
import { Send, Check, Truck } from "lucide-react"
import { SectionHeading } from "./ui/SectionHeading"

/**
 * Data shapes (see data.json → Process / Pricing)
 */
export interface ProcessStep {
	title: string
	text: string
}

export interface PricingRow {
	service: string
	price: string
	highlight?: boolean
}

export interface PricingData {
	rows: PricingRow[]
	note?: string
	deliveryTitle?: string
	delivery?: string
	cta?: string
}

interface PricingProps {
	process?: ProcessStep[]
	pricing?: PricingData
}

/**
 * Pricing Component — "how we work" timeline, indicative price list and delivery/payment terms.
 * Mirrors the static "#pricing" section in public/index.html.
 */
export const Pricing: React.FC<PricingProps> = ({ process, pricing }) => {
	return (
		<section id="pricing" className="lp-section lp-section--soft lp-section--dots">
			<div className="lp-container">
				<SectionHeading
					eyebrow="Spolupráce a ceník"
					title="Jak spolupráce probíhá a kolik stojí"
					lead="Žádné překvapení na faktuře: začínáme konzultací zdarma, pokračujeme analýzou s jasným výstupem a realizaci děláme po krocích, které vidíte průběžně."
				/>

				{process && process.length > 0 && (
					<ol className="lp-steps" style={{ listStyle: "none", padding: 0 }}>
						{process.map((step, idx) => (
							<li
								key={step.title}
								className="lp-step"
								data-reveal=""
								style={{ "--reveal-delay": `${idx * 120}ms` } as React.CSSProperties}
							>
								<span className="lp-step__num" aria-hidden="true">{idx + 1}</span>
								<h3>{step.title}</h3>
								<p>{step.text}</p>
							</li>
						))}
					</ol>
				)}

				<div className="lp-price">
					{pricing && pricing.rows && pricing.rows.length > 0 && (
						<div data-reveal="">
							<h3>Orientační ceník</h3>
							<div className="lp-price__table" role="table" aria-label="Orientační ceník služeb">
								{pricing.rows.map(row => (
									<div
										key={row.service}
										role="row"
										className={`lp-price__row${row.highlight ? " lp-price__row--highlight" : ""}`}
									>
										<span role="cell" className="lp-price__service">
											<Check size={18} strokeWidth={3} /> {row.service}
										</span>
										<span role="cell" className="lp-price__value">{row.price}</span>
									</div>
								))}
							</div>
							{pricing.note && <p className="lp-price__note">{pricing.note}</p>}
						</div>
					)}

					{pricing?.delivery && (
						<div className="lp-price__delivery" data-reveal="">
							<span className="lp-icon-badge" aria-hidden="true">
								<Truck size={24} />
							</span>
							<div>
								<h3>{pricing.deliveryTitle || "Dodání a platba"}</h3>
								<p>
									{pricing.delivery} Podrobnosti upravují{" "}
									<a href="/obchodni-podminky.html">obchodní podmínky</a>.
								</p>
							</div>
						</div>
					)}

					<div className="lp-actions-center" data-reveal="">
						<a href="#contact" className="lp-btn lp-btn--primary lp-btn--lg">
							<Send size={18} /> {pricing?.cta || "Získat nabídku na míru"}
						</a>
					</div>
				</div>
			</div>
		</section>
	)
}
